import { useState, useRef, useEffect } from 'react';
import './ChatBot.css';

/* ══════════════════════════════════════════════════
   AURELIX FULL KNOWLEDGE BASE
══════════════════════════════════════════════════ */
const AURELIX_KB = {
  services: {
    growth: 'Web development, Shopify, CRO, paid media (Meta/Google Ads), SEO, landing pages, LinkedIn campaigns, performance marketing, e-commerce growth.',
    intelligence: 'Marketing analytics, data analytics, business intelligence, custom dashboards, GA4, attribution & funnel analysis, customer insights, SQL, Tableau, data infrastructure.',
    ai: 'AI agents, workflow automations, internal AI tools, AI-powered customer support, knowledge systems, custom AI solutions, agentic workflows.',
  },
  team: {
    abhijeet: 'Abhijeet Singh Jain — Co-Founder, Growth & Analytics. 7+ years experience, EX-American Airlines, managed 20+ brands, Rs.2Cr+ in ad budgets, MS Business Analytics from UT Dallas.',
    ishita: 'Ishita Jain — Co-Founder, UI/UX & CRO. 3+ years, NMIMS graduate, built D2C startups across US & India, expert in Shopify, Figma, A/B Testing.',
  },
  results: '+42% conversion rate improvement, +30% increase in average order value, +40% checkout performance — across 20+ brands.',
  clients: '4mation Skins, 7 Days Natural, Couture By Manju, Economic Granite, Elite Global, HOA Connect, Kriteyu Health, Novacrib, NuboKind (London), Pathivy, Psypathology, Putchi, Sell Abroad, SmartFSA, SuperErgo, Verity Verse.',
  contact: 'Email: hello@aurelixlabs.ai',
  global: 'North America, Europe, Middle East, Asia-Pacific.',
  tagline: 'We connect ads, funnels, and data into one system — focused on revenue.',
};

/* ══════════════════════════════════════════════════
   LANGUAGE DETECTION
══════════════════════════════════════════════════ */
function detectLang(text) {
  if (/[\u0B80-\u0BFF]/.test(text)) return 'ta';   // Tamil script
  if (/[\u0900-\u097F]/.test(text)) return 'hi';   // Hindi/Devanagari
  if (/[\u0C00-\u0C7F]/.test(text)) return 'te';   // Telugu
  if (/[\u0600-\u06FF]/.test(text)) return 'ar';   // Arabic
  if (/[\u4E00-\u9FFF]/.test(text)) return 'zh';   // Chinese

  const lower = text.toLowerCase();
  // Tanglish detection (Tamil words written in English)
  const tanglishWords = ['enna','panra','sollu','paaru','vaa','po','nalla','romba','konjam','epdi','yenna','enna panringa','seri','aama','illai','vanakkam','nandri','kashtam','easy','pannunga','sollunga','theriyum','theriyala'];
  if (tanglishWords.some(w => lower.includes(w))) return 'ta-en'; // Tanglish
  return 'en';
}

/* ══════════════════════════════════════════════════
   INTENT DETECTION
══════════════════════════════════════════════════ */
function detectIntent(text) {
  const t = text.toLowerCase();

  // Complex / needs human — only for genuinely custom requests
  const complexPatterns = [
    'custom proposal', 'quote for', 'detailed plan', 'enterprise', 'partnership',
    'acquisition', 'investment', 'legal', 'contract', 'nda',
    'full audit', 'entire business', 'takeover', 'white label',
  ];
  if (complexPatterns.some(p => t.includes(p))) return 'escalate';

  if (t.match(/service|offer|what do|what can|help me with|do you do/)) return 'services';
  if (t.match(/growth|paid|ads|meta|google|seo|shopify|cro|landing|web/)) return 'growth';
  if (t.match(/analytic|data|dashboard|insight|report|sql|tableau|ga4|intelligence/)) return 'intelligence';
  if (t.match(/ai|automat|agent|workflow|bot|gpt/)) return 'ai';
  if (t.match(/team|founder|who|abhijeet|ishita|about you/)) return 'team';
  if (t.match(/result|case study|proof|success|impact|achiev/)) return 'results';
  if (t.match(/client|brand|work with|portfolio/)) return 'clients';
  if (t.match(/price|cost|how much|budget|rate|package|plan/)) return 'pricing';
  if (t.match(/contact|email|call|reach|start|begin|book|consult/)) return 'contact';
  if (t.match(/where|locat|country|global|region|us|india|uk|dubai/)) return 'global';
  if (t.match(/hello|hi|hey|good|thank|ok|sure|yes|no/)) return 'greeting';

  // Tanglish / Tamil intents
  if (t.match(/enna panr|ena panr|yenna panr|என்ன பண்|what work|what do/)) return 'services';
  if (t.match(/vilai|price|கட்டணம்|budget/)) return 'pricing';
  if (t.match(/யார்|yaar|team|founder|who/)) return 'team';
  if (t.match(/தொடர்பு|contact|email|sollu/)) return 'contact';

  return 'general';
}

/* ══════════════════════════════════════════════════
   REPLIES BY LANGUAGE × INTENT
══════════════════════════════════════════════════ */
const REPLIES = {
  en: {
    greeting: (n) => `Hey${n}! 👋 Happy to help. What would you like to know about Aurelix?`,
    services: (n) => `Aurelix offers three practices${n}:\n\n• **Growth** — Meta/Google Ads, SEO, CRO, Shopify, web dev\n• **Intelligence** — Analytics, dashboards, GA4, data infrastructure\n• **AI** — Agents, automations, custom AI workflows\n\nWhich one fits what you're looking for?`,
    growth: (n) => `Aurelix Growth${n} covers paid media (Meta & Google Ads), SEO, CRO, Shopify development, landing pages, and performance marketing — all built to drive measurable revenue.`,
    intelligence: (n) => `Aurelix Intelligence${n} turns your data into decisions — custom dashboards, GA4, attribution models, SQL-based reporting, and BI systems tailored to your business.`,
    ai: (n) => `Aurelix AI${n} builds custom agents, workflow automations, AI-powered support bots, and internal tools — designed around how your business actually works, not generic templates.`,
    team: (n) => `Aurelix was founded by:\n\n• **Abhijeet** — EX-American Airlines, 7+ years, MS Analytics (UT Dallas), managed Rs.2Cr+ in ad budgets\n• **Ishita** — NMIMS grad, 3+ years in D2C UI/UX, Shopify & CRO expert\n\nTogether they've worked with 20+ brands across India and the US.`,
    results: (n) => `Real results from Aurelix${n}:\n\n• +42% conversion rate improvement\n• +30% increase in average order value\n• +40% checkout performance gains\n\nAcross 20+ brands in D2C, e-commerce, health, and tech.`,
    clients: (n) => `Aurelix has worked with 20+ brands${n} including NuboKind (London), Putchi, SmartFSA, Verity Verse, 7 Days Natural, Economic Granite and more — across D2C, health, real estate, and technology.`,
    pricing: (n) => `Pricing depends on what you need${n}. There's no one-size-fits-all — we scope it based on your goals.\n\nEmail hello@aurelixlabs.ai and we'll put together a plan that fits your budget.`,
    contact: (n) => `Best way to reach us${n}:\n\n📧 hello@aurelixlabs.ai\n\nWe'll get back to you within 24 hours to set up a free strategy call.`,
    global: (n) => `Aurelix works across${n}: North America, Europe, Middle East, and Asia-Pacific — across time zones, industries, and growth stages.`,
    escalate: (n) => `Thanks for reaching out${n}! This sounds like something our team should discuss with you directly.\n\nWe'll get back to you shortly at the contact you provided. Or email hello@aurelixlabs.ai to move faster. 🙌`,
    general: (n) => `Good question${n}! Aurelix helps brands grow through data-driven marketing, analytics, and AI systems. What specifically can I help you with — services, team, results, or getting started?`,
  },

  ta: {
    greeting: (n) => `வணக்கம்${n}! 👋 நான் Aurelix AI. உங்களுக்கு எப்படி உதவலாம்?`,
    services: (n) => `Aurelix மூன்று சேவைகளை வழங்குகிறது${n}:\n\n• **Growth** — Meta/Google Ads, SEO, CRO, Shopify\n• **Intelligence** — Analytics, Dashboards, Data\n• **AI** — Agents, Automations, Custom AI\n\nஎது உங்களுக்கு தேவை?`,
    growth: (n) => `Aurelix Growth${n} — Meta & Google Ads, SEO, CRO, Shopify development, landing pages எல்லாம் cover ஆகும். Revenue grow ஆக design செய்யப்பட்டது.`,
    intelligence: (n) => `Aurelix Intelligence${n} — உங்கள் data-ஐ decisions-ஆக மாற்றுகிறோம். Custom dashboards, GA4, attribution models, SQL reports எல்லாம் உண்டு.`,
    ai: (n) => `Aurelix AI${n} — Custom AI agents, workflow automations, support bots எல்லாம் உங்கள் business-க்கு ஏற்றவாறு build செய்கிறோம்.`,
    team: (n) => `Aurelix-ஐ start பண்ணவர்கள்${n}:\n\n• **Abhijeet** — EX-American Airlines, 7+ years, MS Analytics (UT Dallas)\n• **Ishita** — NMIMS graduate, D2C UI/UX expert, Shopify specialist\n\nஇருவரும் சேர்ந்து 20+ brands-க்கு work பண்ணியிருக்கார்கள்.`,
    results: (n) => `Aurelix-இன் results${n}:\n\n• +42% conversion rate\n• +30% average order value\n• +40% checkout improvement\n\n20+ brands-இல் proven results.`,
    clients: (n) => `Aurelix NuboKind, Putchi, SmartFSA, Verity Verse உட்பட 20+ brands-க்கு work பண்ணியிருக்கிறது${n}.`,
    pricing: (n) => `Price உங்கள் need-ஐ பொறுத்தது${n}. hello@aurelixlabs.ai-க்கு email பண்ணுங்கள் — நாங்கள் உங்களுக்கு suitable plan தருவோம்.`,
    contact: (n) => `எங்களை contact பண்ண${n}:\n\n📧 hello@aurelixlabs.ai\n\n24 மணி நேரத்தில் reply வரும்.`,
    global: (n) => `Aurelix North America, Europe, Middle East, Asia-Pacific என global-ஆக work பண்றது${n}.`,
    escalate: (n) => `நன்றி${n}! இது கொஞ்சம் complex-ஆ இருக்கு — எங்கள் team member உங்களை soon contact பண்ணுவார்கள்.\n\nOrவிரைவாக hello@aurelixlabs.ai-க்கு email பண்ணலாம். 🙌`,
    general: (n) => `நல்ல கேள்வி${n}! Aurelix marketing, analytics, AI மூலம் brands-ஐ grow பண்றது. Services, team, results — எதைப் பத்தி தெரிஞ்சுக்கணும்?`,
  },

  'ta-en': { // Tanglish
    greeting: (n) => `Vanakkam${n}! 👋 Naan Aurelix AI. Enna help panna?`,
    services: (n) => `Aurelix mூன்று services offer pannudu${n}:\n\n• Growth — Meta/Google Ads, SEO, Shopify\n• Intelligence — Analytics, Dashboards\n• AI — Agents, Automations\n\nEthu unga ku venum?`,
    growth: (n) => `Aurelix Growth${n} — paid ads, SEO, CRO, Shopify development ellam pannudu. Revenue grow aaga design pannirukanga.`,
    intelligence: (n) => `Aurelix Intelligence${n} — unga data-ai decisions-a maattrom. Custom dashboards, GA4, reports ellam iruku.`,
    ai: (n) => `Aurelix AI${n} — custom agents, automations, AI bots — unga business-ku fit aaga build pannuvom.`,
    team: (n) => `Aurelix founders${n}:\n\n• Abhijeet — EX-American Airlines, 7+ years, MS Analytics UT Dallas\n• Ishita — NMIMS, D2C UI/UX, Shopify expert\n\nIruvarum 20+ brands-ku work pannirukkanga.`,
    results: (n) => `Aurelix results${n}:\n\n• +42% conversion rate\n• +30% order value\n• +40% checkout improvement\n\n20+ brands la proven!`,
    clients: (n) => `NuboKind, Putchi, SmartFSA, Verity Verse includinг 20+ brands-ku Aurelix work panniruchu${n}.`,
    pricing: (n) => `Price unga need-a pathi irukku${n}. hello@aurelixlabs.ai la email pannu — suitable plan tharuvoam.`,
    contact: (n) => `Contact panna${n}:\n\n📧 hello@aurelixlabs.ai\n\n24 hours la reply varum.`,
    global: (n) => `Aurelix North America, Europe, Middle East, Asia-Pacific la work pannudu${n}.`,
    escalate: (n) => `Nandri${n}! Idhu konjam complex-a iruku — engal team member ungala soon contact panuvanga.\n\nOr hello@aurelixlabs.ai la email pannalaam. 🙌`,
    general: (n) => `Nalla question${n}! Aurelix marketing, analytics, AI use panni brands grow pannudu. Services, team, or results — ethai pathi therinjukanum?`,
  },

  hi: {
    greeting: (n) => `नमस्ते${n}! 👋 मैं Aurelix AI हूं। आपकी कैसे मदद करूं?`,
    services: (n) => `Aurelix तीन services offer करता है${n}:\n\n• **Growth** — Meta/Google Ads, SEO, CRO, Shopify\n• **Intelligence** — Analytics, Dashboards, Data\n• **AI** — Agents, Automations\n\nआप किसमें interested हैं?`,
    growth: (n) => `Aurelix Growth${n} — paid ads, SEO, CRO, Shopify development, landing pages — सब revenue drive करने के लिए designed है।`,
    intelligence: (n) => `Aurelix Intelligence${n} — आपके data को decisions में बदलता है। Custom dashboards, GA4, attribution models, SQL reports।`,
    ai: (n) => `Aurelix AI${n} — custom agents, workflow automations, AI-powered support — आपके business के हिसाब से build किया जाता है।`,
    team: (n) => `Aurelix के founders${n}:\n\n• **Abhijeet** — EX-American Airlines, 7+ साल, MS Analytics (UT Dallas)\n• **Ishita** — NMIMS graduate, D2C UI/UX expert\n\nदोनों ने 20+ brands के साथ काम किया है।`,
    results: (n) => `Aurelix के results${n}:\n\n• +42% conversion rate\n• +30% average order value\n• +40% checkout improvement\n\n20+ brands में proven।`,
    clients: (n) => `Aurelix ने NuboKind, Putchi, SmartFSA, Verity Verse सहित 20+ brands के साथ काम किया है${n}।`,
    pricing: (n) => `Pricing आपकी ज़रूरत पर depend करती है${n}। hello@aurelixlabs.ai पर email करें — हम आपके लिए plan बनाएंगे।`,
    contact: (n) => `Contact करने के लिए${n}:\n\n📧 hello@aurelixlabs.ai\n\n24 घंटे में reply मिलेगा।`,
    global: (n) => `Aurelix North America, Europe, Middle East, Asia-Pacific में काम करता है${n}।`,
    escalate: (n) => `शुक्रिया${n}! यह थोड़ा complex लग रहा है — हमारी team जल्द ही आपसे contact करेगी।\n\nया hello@aurelixlabs.ai पर email करें। 🙌`,
    general: (n) => `अच्छा सवाल${n}! Aurelix marketing, analytics और AI से brands को grow करता है। Services, team, results — किसके बारे में जानना है?`,
  },
};

/* ══════════════════════════════════════════════════
   SYSTEM PROMPT (with OpenAI key)
══════════════════════════════════════════════════ */
const buildSystemPrompt = (lead) => `
You are Aurelix AI, the smart conversational assistant for Aurelix agency.

COMPANY: Aurelix — performance marketing, data analytics & AI automation.
SERVICES: Growth (Meta/Google Ads, SEO, CRO, Shopify), Intelligence (analytics, dashboards, GA4, SQL, Tableau), AI (agents, automations, workflows).
TEAM: Abhijeet Singh Jain (Co-Founder, EX-American Airlines, 7+ years, MS Analytics UT Dallas), Ishita Jain (Co-Founder, NMIMS, D2C UI/UX, Shopify).
RESULTS: +42% CVR, +30% AOV, +40% checkout improvement across 20+ brands.
CLIENTS: NuboKind, Putchi, SmartFSA, Verity Verse, 4mation Skins, Economic Granite and 15+ more.
GLOBAL: North America, Europe, Middle East, Asia-Pacific.
CONTACT: hello@aurelixlabs.ai

LEAD: Name=${lead.name}, Company=${lead.company}, Location=${lead.place}, Contact=${lead.contact}

LANGUAGE RULE: Detect the user's language and ALWAYS reply in the SAME language.
- Hindi → reply in Hindi
- Tamil script → reply in Tamil
- Tanglish (Tamil in English letters like "enna panringa") → reply in Tanglish
- Arabic → Arabic, French → French, Spanish → Spanish
- Mixed/unclear → English

ESCALATION RULE: Only say "our team will reach out" for genuinely complex custom requests (enterprise proposals, white-label, acquisition, legal, NDA). For ALL other questions — answer directly and helpfully.

Be concise (2-4 sentences), warm, and use the user's name naturally.`;

/* ══════════════════════════════════════════════════
   LEAD STEPS
══════════════════════════════════════════════════ */
const LEAD_STEPS = [
  { key: 'name',    msg: `Hi there! 👋 I'm Aurelix AI.\n\nBefore we start, what's your name?`,        ph: 'Your name...' },
  { key: 'company', msg: (l) => `Nice to meet you, ${l.name}! 😊\nWhat company or brand are you with?`, ph: 'Company name...' },
  { key: 'place',   msg: () => 'Where are you based?',                                                   ph: 'City / Country...' },
  { key: 'contact', msg: () => 'What\'s the best email or phone to reach you?',                          ph: 'Email or phone...' },
];

/* ══════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════ */
export default function ChatBot() {
  const [open, setOpen]         = useState(false);
  const [leadStep, setLeadStep] = useState(0);
  const [lead, setLead]         = useState({});
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [unread, setUnread]     = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    setMessages([{ role: 'assistant', content: LEAD_STEPS[0].msg }]);
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);
  useEffect(() => {
    if (open) { setUnread(false); setTimeout(() => inputRef.current?.focus(), 300); }
  }, [open]);

  const pushBot = (content) => {
    setMessages(prev => [...prev, { role: 'assistant', content }]);
    if (!open) setUnread(true);
  };

  const send = async (text) => {
    const content = (text || input).trim();
    if (!content || loading) return;
    setInput('');
    const history = [...messages, { role: 'user', content }];
    setMessages(history);

    /* ── Lead capture ── */
    if (leadStep < LEAD_STEPS.length) {
      const key = LEAD_STEPS[leadStep].key;
      const updatedLead = { ...lead, [key]: content };
      setLead(updatedLead);
      const next = leadStep + 1;
      setLeadStep(next);
      await new Promise(r => setTimeout(r, 400));
      if (next < LEAD_STEPS.length) {
        const q = LEAD_STEPS[next].msg;
        pushBot(typeof q === 'function' ? q(updatedLead) : q);
      } else {
        pushBot(`Thanks ${updatedLead.name}! 🎉 You're all set.\n\nAsk me anything about Aurelix — in any language! Hindi, Tamil, Tanglish, Arabic, French... I've got you. 🌍`);
      }
      return;
    }

    /* ── AI answer ── */
    setLoading(true);
    try {
      const apiKey = process.env.REACT_APP_OPENAI_KEY;
      if (apiKey) {
        const res = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
          body: JSON.stringify({
            model: 'gpt-4o-mini',
            messages: [
              { role: 'system', content: buildSystemPrompt(lead) },
              ...history.slice(-12).map(m => ({ role: m.role, content: m.content })),
            ],
            max_tokens: 280,
            temperature: 0.75,
          }),
        });
        const data = await res.json();
        pushBot(data.choices?.[0]?.message?.content || 'Sorry, please try again or email hello@aurelixlabs.ai');
      } else {
        /* Smart fallback */
        await new Promise(r => setTimeout(r, 650));
        const lang  = detectLang(content);
        const intent = detectIntent(content);
        const R = REPLIES[lang] || REPLIES.en;
        const fn = R[intent] || R.general;
        const name = lead.name ? `, ${lead.name}` : '';
        pushBot(fn(name));
      }
    } catch {
      pushBot('Something went wrong. Please email hello@aurelixlabs.ai');
    } finally {
      setLoading(false);
    }
  };

  const progress = Math.min((leadStep / LEAD_STEPS.length) * 100, 100);
  const ph = leadStep < LEAD_STEPS.length ? LEAD_STEPS[leadStep].ph : 'Ask me anything... 🌍';

  return (
    <>
      <button className={`cb-fab ${open ? 'cb-fab--open' : ''}`} onClick={() => setOpen(o => !o)} aria-label={open ? 'Close' : 'Chat'}>
        {open
          ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        }
        {!open && <span className="cb-fab-pulse" />}
        {!open && unread && <span className="cb-unread-dot" />}
      </button>

      <div className={`cb-window ${open ? 'cb-window--open' : ''}`}>
        <div className="cb-header">
          <div className="cb-header-info">
            <div className="cb-header-avatar">A</div>
            <div>
              <p className="cb-header-name">Aurelix AI</p>
              <p className="cb-header-status">
                <span className="cb-online-dot" />
                {leadStep < LEAD_STEPS.length ? `Step ${leadStep + 1} of ${LEAD_STEPS.length}` : 'Online · Multilingual 🌍'}
              </p>
            </div>
          </div>
          <button className="cb-close" onClick={() => setOpen(false)} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="cb-progress"><div className="cb-progress-fill" style={{ width: `${progress}%` }} /></div>

        <div className="cb-messages">
          {messages.map((m, i) => (
            <div key={i} className={`cb-msg cb-msg--${m.role === 'user' ? 'user' : 'bot'}`}>
              <p style={{ whiteSpace: 'pre-line' }}>{m.content}</p>
            </div>
          ))}
          {loading && (
            <div className="cb-msg cb-msg--bot">
              <div className="cb-typing"><span /><span /><span /></div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <form className="cb-input-row" onSubmit={e => { e.preventDefault(); send(); }}>
          <input ref={inputRef} className="cb-input" value={input} onChange={e => setInput(e.target.value)} placeholder={ph} disabled={loading} autoComplete="off" />
          <button type="submit" className="cb-send" disabled={!input.trim() || loading} aria-label="Send">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </form>
        <p className="cb-footer-note">Aurelix AI · hello@aurelixlabs.ai</p>
      </div>
    </>
  );
}
